import React, { memo, useCallback, useContext, useMemo } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { ScaChallenger } from '@bingads-webui-cc-react/pidl-payment-method-actions-base';
import { PaasChallenger } from '@bingads-webui-campaign-react/payment-as-a-service-challenge';
import * as paymentAsAServiceApiRaw from '@bingads-webui-campaign-react/payment-as-a-service-api';
import AjaxPromise from '@bingads-webui-clientcenter/ajax-promise-smart';
import MsaAuth from '@bingads-webui-clientcenter/msa-auth-smart';
import PidlService from '@bingads-webui-clientcenter/pidl-service-smart';
import PifdAuth from '@bingads-webui-clientcenter/pifd-auth-smart';
import IWindow from '@bingads-webui-clientcenter/window-interface';
import { PaymentContext } from '@bingads-webui-cc-react/payment-context';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';

import { PropTypeI18n, PropTypesPaymentProps } from './shapes';

const getAntiForgeryToken = () => {
  const antiForgeryTokenInput = IWindow.document.querySelector('input[name="__RequestVerificationToken"]');
  return antiForgeryTokenInput && antiForgeryTokenInput.value;
};

const ChallengerUnwrapped = ({
  isPAAS,
  aid,
  cid,
  i18n,
  paymentAmount,
  currency,
  piCountry,
  CtpPiId,
  MtPiId,
  success,
  fail,
  cancel,
  shouldRetryWithPopup,
  shouldAuthenticateWithPopup,
  paymentProps: {
    ccBaseUrl,
    restfulService,
    isPsd2ValidationEnabled,
    culture,
    pidlLoggerCreate = _.noop,
  },
}) => {
  const antiForgeryToken = useMemo(getAntiForgeryToken, []);
  const {
    paymentContext: {
      PidlConfiguration: {
        MsaDelegateAuthUrl,
        PidlEnabledCountries,
        ExcludedCountriesByCardType,
        PidlErrorFormattingStrings,
        ThreatMetrixUrl,
        PidlSupportedCultureMapping,
        PidlEnvironment,
        PidlScaEmulatorTestHeader,
      },
      AuthType,
      GreenIdUrl,
      CookieDomain,
      CookiePath,
    },
  } = useContext(PaymentContext);

  const permissions = useContext(PermissionsContext);

  const ajaxPromise = useMemo(
    () => new AjaxPromise({ restService: restfulService }),
    [restfulService]
  );
  const pifdAuth = useCallback(
    () => PifdAuth(ajaxPromise, ccBaseUrl),
    [ajaxPromise, ccBaseUrl]
  );
  const msaAuth = useMemo(
    () => new MsaAuth({
      cookieDomain: CookieDomain,
      cookiePath: CookiePath,
      delegateAuthUrl: MsaDelegateAuthUrl,
      shouldRetryWithPopup,
      shouldAuthenticateWithPopup,
    }),
    [CookieDomain, CookiePath, MsaDelegateAuthUrl, shouldAuthenticateWithPopup, shouldRetryWithPopup]
  );
  const pidlService = useMemo(
    () => new PidlService({
      ccBaseUrl,
      ajaxPromise,
    }),
    [ajaxPromise, ccBaseUrl]
  );

  const piData = useMemo(
    () => ({
      CtpPiId,
      MtPiId,
      PiType: 'CreditCard', // Pidl Enum
    }),
    [CtpPiId, MtPiId]
  );
  const purchaseContext = useMemo(
    () => ({
      amount: paymentAmount,
      currency,
      country: piCountry,
      challengeScenario: 'PaymentTransaction', // Pidl Enum
    }),
    [currency, paymentAmount, piCountry]
  );
  const eventHandlers = useMemo(
    () => ({
      handleTerminatingError: error => fail(error.ErrorMessage || i18n.getString(_TL_("We've encountered an error and can't continue. Please contact support for further assistance."))),
      handleProcessingStart: _.noop, // no action is currently required
      handlePidlDocRendered: _.noop, // no action is currently required
      handleProcessingComplete: _.noop, // no action is currently required
      handleGoHome: cancel,
      handleScaChallengeSuccess: success,
      handleScaChallengeFailure: () => fail(i18n.getString(_TL_("We're sorry, but we've encountered a problem with your card authentication. Please try again. If that doesn't fix the problem, contact your bank for assistance."))),
    }),
    [cancel, fail, i18n, success]
  );

  if (isPAAS) {
    const paymentAsAServiceApi = _.mapObject(paymentAsAServiceApiRaw, api => _.partial(api, ajaxPromise));

    const paasEventHandlers = {
      handleDataLoading: eventHandlers.handleProcessingStart,
      handleDataLoaded: eventHandlers.handleProcessingComplete,
      handleScaChallengeSuccess: (sessionId) => {
        success({ sessionId });
      },
      handleScaChallengeFailure: eventHandlers.handleScaChallengeFailure,
      handleCancel: eventHandlers.handleGoHome,
    };

    const pidlLogger = pidlLoggerCreate('AddFunds', 'ScaChallenge');

    return (
      <PaasChallenger
        paymentAsAServiceApi={paymentAsAServiceApi}
        aid={aid}
        cid={cid}
        mtPiId={MtPiId}
        chargeAmount={paymentAmount}
        i18n={i18n}
        pidlEnvironment={PidlEnvironment}
        eventHandlers={paasEventHandlers}
        pidlLogger={pidlLogger}
        isPidlSecureFieldsIsolationAdoptionEnabled={_.get(permissions, ['dynamic', 'PidlSecureFieldsIsolationAdoption'], false)}
      />
    );
  }

  return (
    <ScaChallenger
      i18n={i18n}
      piData={piData}
      purchaseContext={purchaseContext}
      eventHandlers={eventHandlers}
      isPsd2ValidationEnabled={isPsd2ValidationEnabled}
      msaAuth={msaAuth}
      pifdAuth={pifdAuth}
      pidlService={pidlService}
      verificationToken={antiForgeryToken}
      enabledCountries={PidlEnabledCountries}
      excludedCountriesByCard={ExcludedCountriesByCardType}
      pidlErrorFormatMapping={PidlErrorFormattingStrings}
      authenticationType={AuthType}
      ThreatMetrixUrl={ThreatMetrixUrl}
      GreenIdUrl={GreenIdUrl}
      culture={culture}
      supportedCultureMapping={PidlSupportedCultureMapping}
      pidlEnvironment={PidlEnvironment}
      currency={currency}
      challengerTestHeader={PidlScaEmulatorTestHeader}
      permissions={permissions}
    />
  );
};

ChallengerUnwrapped.propTypes = {
  isPAAS: PropTypes.bool,
  aid: PropTypes.number,
  cid: PropTypes.number,
  i18n: PropTypeI18n.isRequired,
  paymentAmount: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  piCountry: PropTypes.string.isRequired,
  CtpPiId: PropTypes.string.isRequired,
  MtPiId: PropTypes.string.isRequired,
  success: PropTypes.func.isRequired,
  fail: PropTypes.func.isRequired,
  cancel: PropTypes.func.isRequired,
  paymentProps: PropTypesPaymentProps.isRequired,
  shouldRetryWithPopup: PropTypes.bool,
  shouldAuthenticateWithPopup: PropTypes.bool,
};

ChallengerUnwrapped.defaultProps = {
  isPAAS: false,
  aid: null,
  cid: null,
  shouldRetryWithPopup: false,
  shouldAuthenticateWithPopup: false,
};

export const Challenger = memo(ChallengerUnwrapped);
